import { useRef, useEffect, useCallback } from "react";
import { useFlushOnPageHide } from "@/hooks/use-flush-on-page-hide";
import { useMutation } from "@apollo/client";
import { UPDATE_CLIP_DATA } from "@/queries/queries";
import type { QuoteCarousel } from "@/lib/quoteCarousel/types";

interface Options {
  clipId: string | null;
  carousel: QuoteCarousel | null;
  /** The clip the CURRENT `carousel` belongs to — same stale-window tag as
   *  useAutoSaveClipData; see the rationale there. */
  loadedClipId: string | null;
  enabled: boolean;
  delay?: number;
}

type PendingWrite = {
  clipId: string;
  carousel: QuoteCarousel;
  serialized: string;
};

/** Debounced persistence of the per-clip quote carousel draft
 *  (clip.data.quoteCarousel). Clone of useAutoSaveCoverImage, with ONE
 *  difference: `null` means "not loaded yet" here, not a real value to
 *  persist — writes are skipped entirely while `carousel === null` (there is
 *  no "clear the carousel via null" affordance through this hook).
 *  Pending writes are flushed (never dropped) on clip switch, unmount, and
 *  page-hide. */
export function useAutoSaveQuoteCarousel({
  clipId,
  carousel,
  loadedClipId,
  enabled,
  delay = 500,
}: Options) {
  const [updateClipData, { loading: saving }] = useMutation(UPDATE_CLIP_DATA);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<PendingWrite | null>(null);
  const lastSavedRef = useRef<string>("");
  const awaitingLoadRef = useRef<boolean>(true);
  const currentClipIdRef = useRef<string | null>(null);

  const performSave = useCallback(
    async (write: PendingWrite) => {
      try {
        const payload = { quoteCarousel: write.carousel };
        await updateClipData({
          variables: { id: write.clipId, data: payload },
        });
        lastSavedRef.current = write.serialized;
      } catch (error) {
        console.error("Auto-save quote carousel failed:", error);
      }
    },
    [updateClipData],
  );

  /** Fire the pending write NOW (fire-and-forget). Never drops it. */
  const firePending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) void performSave(pending);
  }, [performSave]);
  // Tab close never unmounts: fire the pending write as a keepalive request.
  useFlushOnPageHide(firePending);
  const firePendingRef = useRef(firePending);
  useEffect(() => {
    firePendingRef.current = firePending;
  }, [firePending]);

  // Clip switch: flush the old clip's pending write, await the new load.
  useEffect(() => {
    if (clipId !== currentClipIdRef.current) {
      firePending();
      currentClipIdRef.current = clipId;
      awaitingLoadRef.current = true;
      lastSavedRef.current = "";
    }
  }, [clipId, firePending]);

  useEffect(() => {
    if (!enabled || !clipId) return;
    // Stale window: this carousel belongs to another clip.
    if (loadedClipId !== clipId) return;
    // `null` means "not loaded yet" — skip entirely, never write it.
    if (carousel === null) return;

    const serialized = JSON.stringify(carousel);
    if (awaitingLoadRef.current) {
      awaitingLoadRef.current = false;
      lastSavedRef.current = serialized;
      return;
    }
    if (serialized === lastSavedRef.current) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pendingRef.current = { clipId, carousel, serialized };
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) void performSave(pending);
    }, delay);
    // Clears only the timer; pendingRef survives for the unmount flush.
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [carousel, loadedClipId, clipId, enabled, delay, performSave]);

  // Unmount: flush instead of dropping.
  useEffect(() => () => firePendingRef.current(), []);

  return { saving, flush: firePending };
}
